import type { ReactNode } from 'react';
import { useEffect } from 'react';

import { GalaxyHero } from '@dymaxion/galaxy';
import {
  useRuntimeConfig,
  useSessionStore,
  scheduleProactiveRefresh,
} from '@dymaxion/auth';

import { NavBar } from './NavBar';

/**
 * AppShell wraps every route. Provides the Spline 3D hero backdrop,
 * a glass navbar, a fixed-height flex layout, and a glass footer.
 * Routes render in the main content slot.
 *
 * All host-specific values (Spline scene URL, footer links, brand
 * label) come from the runtime config loaded in main.tsx — no
 * hardcoded URLs in this component.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const config = useRuntimeConfig();
  const { hero, links, brand } = config.dymaxionFrontend;
  const claimsExp = useSessionStore((s) => s.claims?.exp);

  // AWSDO-1316: proactive silent-refresh timer. When the session has
  // a known expiry, schedule a refresh 5 min before it. The interceptor
  // in @dymaxion/auth/api.ts is the safety net for 401s — this timer
  // is the foreground optimization that avoids the cookie-expiry round
  // trip for users with an open Grafana monitoring tab.
  useEffect(() => {
    if (!claimsExp) return undefined;
    return scheduleProactiveRefresh(claimsExp, 300);
  }, [claimsExp]);

  return (
    <div className="relative min-h-screen flex flex-col bg-black">
      <NavBar publicSiteUrl={links.publicSite} contactUrl={links.contactUrl} />
      <GalaxyHero scene={hero.splineScene} />

      {/*
       * Main layout:
       *  - flex-1 takes the space between NavBar and footer
       *  - `flex items-center` vertically centres the inner wrapper
       *    (fixes the "Top Right" positioning bug where the form
       *    stuck to the top because the previous grid h-full didn't
       *    resolve against the flex parent)
       *  - Desktop (md+): form anchored to the RIGHT half
       *  - Mobile (<md): form stacks vertically, horizontally centred
       *  - pointer-events-none on the wrapper lets the Spline scene
       *    stay interactive in the empty area; the form re-enables
       *    pointer events on its own wrapper
       */}
      {/*
       * Scene-text mask — the Spline scene has baked-in text ("Effortless
       * AI Integration for business") and a "JOIN US NOW" button that
       * cannot be removed from outside the .splinecode asset. We cover
       * those regions with a soft-edged black panel on the left half of
       * the viewport. The orb sits bottom-right so the right half stays
       * clear. The panel also acts as the visual anchor for the login
       * card which is centred on top of it.
       */}
      <div
        aria-hidden="true"
        className="absolute inset-y-0 left-0 z-[5] hidden md:block pointer-events-none"
        style={{
          // Mask burying the Spline scene's baked-in "Effortless AI
          // Integration for business" + "JOIN US NOW" (Sally measured
          // text bbox x=[248,631], button y=[540,673], orb x=[900,1350]).
          // Stays fully opaque across the whole text/button region and
          // only fades in the ~130px gap before the orb starts.
          //   width: 60% of viewport
          //   opaque (0.995) from 0 → 85% of the mask (= 51% viewport)
          //   fade 85% → 100% (51% → 60% viewport)
          //   clear 60%+ viewport → orb region untouched
          // At 1440px: opaque to x=734, clear at x=864, orb at x=900+.
          // At 1920px: opaque to x=979, clear at x=1152, orb at x=1200+.
          // At 1280px: opaque to x=653, clear at x=768, orb at x=800+.
          width: '60%',
          background:
            'linear-gradient(to right, rgba(0,0,0,0.995) 0%, rgba(0,0,0,0.995) 85%, rgba(0,0,0,0) 100%)',
        }}
      />

      {/*
       * Scene-wave mask — the Spline scene also bakes in a dim undulating
       * graphic beneath the orb around y=790–925 on the right side of the
       * viewport. Karim wants that graphic moved to the bottom of the
       * screen. Because it lives inside the .splinecode asset it cannot be
       * repositioned from outside, so we mask it out here and re-render a
       * fresh SVG wave anchored just above the footer (below).
       */}
      <div
        aria-hidden="true"
        className="absolute left-0 right-0 z-[5] hidden md:block pointer-events-none"
        style={{
          top: '78%',
          height: '15%',
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.98) 25%, rgba(0,0,0,0.98) 75%, rgba(0,0,0,0) 100%)',
        }}
      />

      {/*
       * Bottom terrain — soft filled ridge that mirrors the Spline scene's
       * baked-in terrain visual language: a low-contrast dark-cyan hill
       * silhouette fading into black, no bright stroked edges. Two stacked
       * filled paths give a parallax depth feel; a vertical fade-to-black
       * gradient sinks the base into the footer the same way the Spline
       * terrain dissolves into the page.
       */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-0 right-0 z-[5] overflow-hidden"
        style={{ bottom: '92px', height: '180px' }}
      >
        <svg
          viewBox="0 0 2880 200"
          preserveAspectRatio="none"
          className="absolute left-0 top-0 h-full"
          style={{
            width: '200%',
            animation: 'dymaxion-wave-drift 14s ease-in-out infinite alternate',
          }}
        >
          <defs>
            {/* Vertical fade — ridge top is dim teal, base sinks to pure black */}
            <linearGradient id="dymaxion-terrain-fill" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#0BB5BA" stopOpacity="0.18" />
              <stop offset="55%" stopColor="#0BB5BA" stopOpacity="0.06" />
              <stop offset="100%" stopColor="#000000" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="dymaxion-terrain-back" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#0BB5BA" stopOpacity="0.10" />
              <stop offset="60%" stopColor="#0BB5BA" stopOpacity="0.03" />
              <stop offset="100%" stopColor="#000000" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Back ridge — wider, softer hill silhouette */}
          <path
            fill="url(#dymaxion-terrain-back)"
            d="M0,140 C320,70 640,110 960,90 C1280,70 1600,120 1920,95 C2240,70 2560,115 2880,90 L2880,200 L0,200 Z"
          />
          {/* Front ridge — slightly taller, broader sweeping mound */}
          <path
            fill="url(#dymaxion-terrain-fill)"
            d="M0,160 C240,110 480,130 720,105 C960,80 1200,125 1440,100 C1680,75 1920,130 2160,110 C2400,90 2640,135 2880,115 L2880,200 L0,200 Z"
          />
        </svg>
        <style>{`
          @keyframes dymaxion-wave-drift {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-25%); }
          }
        `}</style>
      </div>

      <main className="relative z-10 flex-1 flex items-center pt-24 pb-10 sm:pt-28 sm:pb-14 pointer-events-none">
        <div className="container mx-auto w-full px-4 md:px-6 lg:px-8">
          <div className="flex justify-center md:justify-start">
            <div className="pointer-events-auto w-full max-w-[460px] md:ml-6 lg:ml-12">
              {children}
            </div>
          </div>
        </div>
      </main>

      <footer className="relative z-10 border-t border-white/10 bg-black/20 px-6 py-5 sm:px-8 sm:py-6 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl flex-col gap-2 text-xs text-white/60 sm:flex-row sm:items-center sm:justify-between">
          <div>
            &copy; 2026{' '}
            <span className="font-medium text-white">{brand.wordmark}</span> &mdash; {brand.tagline}
          </div>
          <nav className="flex gap-4">
            <a href={links.publicSite} className="hover:text-primary transition-colors">
              {hostnameOf(links.publicSite)}
            </a>
            <a href={links.privacyUrl} className="hover:text-primary transition-colors">
              Privacy
            </a>
            <a href={links.termsUrl} className="hover:text-primary transition-colors">
              Terms
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

/**
 * Extract the hostname from a URL string. Falls back to the raw value
 * if the URL constructor fails (e.g. relative URLs in mis-configured
 * tenant configs) so the footer still renders something useful.
 */
function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}
